import type { ReactNode } from 'react';
import { ParallelUnitsDropdown } from 'components/ParallelUnitsDropdown';
import type { WarningsInterface } from 'store/simulation/types';
import { SwitchWithLabel } from './SwitchWithLabel';
import { InfoBar } from './InfoBar';
import styled from '@emotion/styled';
import WarningsPanelAccordion from './WarningsPanelAccordion';

export const TopCardContent = styled('div')`
  position: relative;
  width: 100%;
`;

export const TopCardAttributeContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin: ${(p) => p.theme.spacing(1)} 16px;
  font-size: 14px;
`;

export const TopCardTextContainer = styled('div')`
  display: flex;
  flex-direction: row;
  padding-top: ${(p) => p.theme.spacing(1)};
  padding-bottom: ${(p) => p.theme.spacing(1)};
`;

export const TopCardLabel = styled('div')`
  font-weight: 500;
  min-width: 138px;
  overflow: hidden;
  color: ${(p) => p.theme.palette.grey['800']};
`;

export const TopCardValue = styled('div')`
  font-weight: 400;
  max-width: 115px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

export const TopCardExtraInformation = styled('div')`
  font-size: 14px;
  font-weight: 400;
  padding-top: ${(p) => p.theme.spacing(1)};
  padding-bottom: ${(p) => p.theme.spacing(1)};
  max-width: 252px;
  color: ${(p) => p.theme.palette.grey['800']};
`;

export const TopCardFooter = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
`;

export const TopCardImage = styled('img')`
  position: absolute;
  width: 160px;
  top: 0px;
  right: -100px;
`;

type CardInterface = {
  children?: ReactNode;
  lastCard?: boolean;
  expandValue?: boolean;
  mirroredImageValue?: boolean;
  attributes?: { label: string; value: string }[] | undefined;
  extraInformation?: string;
  extraInformation2?: string | undefined;
  mirroredImage?: boolean;
  parallelUnits?: number;
  setParallelUnits?(val: number): void;
  isSurgeBinOneOutput?: boolean;
  isSurgeBinTwoOutputs?: boolean;
  storageExtraSwitchLabel?: string;
  storageExtraSwitchValue?: boolean;
  setStorageExtraSwitchValue?(val: boolean): void;
  storageInfoTitle?: string;
  warnings?: WarningsInterface | undefined;
  imgSrc?: string;
  imgMenu?: ReactNode;
  menu?: ReactNode;
  disabled?: boolean;
};

export const TopCard = ({
  children,
  attributes,
  warnings,
  imgSrc,
  extraInformation,
  extraInformation2,
  parallelUnits,
  setParallelUnits,
  storageInfoTitle,
  storageExtraSwitchValue,
  setStorageExtraSwitchValue,
  storageExtraSwitchLabel,
  isSurgeBinOneOutput,
  isSurgeBinTwoOutputs,
  disabled,
  mirroredImage = false,
}: CardInterface) => {
  const showTopCardContent =
    !!imgSrc || (!!attributes && attributes.length > 0);

  return (
    <div>
      {showTopCardContent && (
        <TopCardContent>
          {imgSrc && (
            <TopCardImage
              style={{
                transform: mirroredImage ? 'scaleX(-1)' : 'scaleX(1)',
              }}
              alt='img'
              src={imgSrc}
            />
          )}
          <TopCardAttributeContainer
            style={{ minHeight: imgSrc ? '144px' : '0px' }}
          >
            {attributes?.map(({ label, value }) => (
              <TopCardTextContainer key={label}>
                <TopCardLabel id='TopCardLabel'>{label}</TopCardLabel>
                <TopCardValue>{value}</TopCardValue>
              </TopCardTextContainer>
            ))}
            <TopCardExtraInformation>
              {extraInformation}
            </TopCardExtraInformation>
            {extraInformation2 !== undefined && (
              <TopCardExtraInformation>
                {extraInformation2}
              </TopCardExtraInformation>
            )}
          </TopCardAttributeContainer>
        </TopCardContent>
      )}
      {children}
      <WarningsPanelAccordion warnings={warnings} />
      {!!parallelUnits && !!setParallelUnits && (
        <TopCardFooter>
          <ParallelUnitsDropdown
            parallelUnits={parallelUnits}
            setParallelUnits={(val) => setParallelUnits(val)}
          />
          {storageInfoTitle &&
            storageExtraSwitchValue !== undefined &&
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            (isSurgeBinOneOutput || isSurgeBinTwoOutputs) &&
            setStorageExtraSwitchValue &&
            storageExtraSwitchLabel && (
              <>
                <SwitchWithLabel
                  value={storageExtraSwitchValue}
                  setValue={(val) => setStorageExtraSwitchValue(val)}
                  label={storageExtraSwitchLabel}
                  disabled={disabled}
                />
                <InfoBar type='information' title={storageInfoTitle} />{' '}
              </>
            )}
        </TopCardFooter>
      )}
    </div>
  );
};
